import type { ScenarioSimulateInput, PortfolioScenarioComparison } from '@helix/lib/portfolio-planner-scenario-contract';
import { apiFetch } from './apiFetch';

export class PortfolioPlannerApiError extends Error {
  status: number;
  body: string;

  constructor(message: string, opts: { status: number; body: string }) {
    super(message);
    this.status = opts.status;
    this.body = opts.body;
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    const message = text || `Request failed: ${res.status}`;
    throw new PortfolioPlannerApiError(message, { status: res.status, body: text });
  }
  return (await res.json()) as T;
}

export type PortfolioPlannerScopesResponse = {
  tenantId: string;
  leadScopes: Array<{
    scopeRootNodeId: string;
    label?: string;
    headcount?: number;
    title?: string | null;
    unitName?: string | null;
    personName?: string | null;
  }>;
  enterpriseRollupAllowed: boolean;
  defaultView:
    | { kind: 'planner'; scopeRootNodeId: string }
    | { kind: 'enterprise_rollup'; scopeRootNodeId: null }
    | { kind: 'blocked'; scopeRootNodeId: null; reason: string };
  defaultScopeRootNodeId: string | null;
};

export async function getPortfolioPlannerScopes(): Promise<PortfolioPlannerScopesResponse> {
  const res = await apiFetch('/v1/portfolio-planner/scopes', { cache: 'no-cache' });
  return handle<PortfolioPlannerScopesResponse>(res);
}

export function resolveDefaultPortfolioPlannerScopeRootNodeId(
  scopes: PortfolioPlannerScopesResponse,
): string | null {
  return (
    scopes.defaultScopeRootNodeId ??
    (Array.isArray(scopes.leadScopes) ? scopes.leadScopes[0]?.scopeRootNodeId ?? null : null)
  );
}

export async function getActiveOrgStateId(): Promise<string | null> {
  const res = await apiFetch('/v1/org/state/active', { cache: 'no-cache' });
  if (res.status === 204) return null;
  const payload = await handle<{ orgStateId: string | null }>(res);
  return payload?.orgStateId ?? null;
}

export async function getPortfolioPlannerActivePlan(params: { strategyId: string; scopeRootNodeId: string }): Promise<{
  strategyId: string;
  scopeRootNodeId: string;
  planId: string | null;
  resolution: 'ACTIVE_POINTER' | 'LATEST_PUBLISH_EVENT' | 'LATEST_PLAN' | 'NONE';
}> {
  const qs = new URLSearchParams({ strategyId: params.strategyId });
  const res = await apiFetch(`/v1/portfolio-planner/scopes/${encodeURIComponent(params.scopeRootNodeId)}/active-plan?${qs.toString()}`, {
    cache: 'no-cache',
  });
  return handle(res);
}

export async function recordPortfolioPlannerVerificationSnapshotLatest(params: {
  strategyId: string;
  scopeRootNodeId: string;
}): Promise<{ snapshotId: string; asOf: string; planVersion: number }> {
  const strategyId = String(params.strategyId ?? '').trim();
  const scopeRootNodeId = String(params.scopeRootNodeId ?? '').trim();
  if (!strategyId) throw new Error('Missing strategyId');
  if (!scopeRootNodeId) throw new Error('Missing scopeRootNodeId');

  const res = await apiFetch(
    `/v1/portfolio-planner/scopes/${encodeURIComponent(scopeRootNodeId)}/verification-snapshots/record-latest`,
    {
      method: 'POST',
      cache: 'no-cache',
      json: { strategyId },
    },
  );
  return handle(res);
}

export async function listPortfolioPlannerPlans(params: { strategyId: string; scopeRootNodeId: string }): Promise<{
  plans: Array<{
    planId: string;
    strategyId: string;
    scopeRootNodeId: string;
    asOf: string;
    kernelVersion: string;
    inputsHash: string;
    sourceHash: string;
    createdAt: string;
  }>;
}> {
  const qs = new URLSearchParams({ strategyId: params.strategyId, scopeRootNodeId: params.scopeRootNodeId });
  const res = await apiFetch(`/v1/portfolio-planner/plans?${qs.toString()}`, { cache: 'no-cache' });
  return handle(res);
}

export type PortfolioPlannerBacktestHistoryResponse = {
  schemaVersion: 'portfolio_planner_history_backtest_v1';
  generatedAt: string;
  strategyId: string;
  scopeRootNodeId: string;
  report: Record<string, unknown>;
  plans: Array<{
    planId: string;
    publishedAt: string;
    asOf: string;
    selectionCount: number;
    portfolioHealthScore: number | null;
    optimizerValueAddUsd: number | null;
  }>;
};

export async function getPortfolioPlannerBacktestHistory(params: {
  strategyId: string;
  scopeRootNodeId: string;
  limit?: number;
}): Promise<PortfolioPlannerBacktestHistoryResponse> {
  const qs = new URLSearchParams({ strategyId: params.strategyId, scopeRootNodeId: params.scopeRootNodeId });
  if (params.limit != null) qs.set('limit', String(params.limit));
  const res = await apiFetch(`/v1/portfolio-planner/history/backtest?${qs.toString()}`, { cache: 'no-cache' });
  return handle(res);
}

export type PortfolioPlannerAssumptions = {
  status: 'MISSING' | 'CONFIGURED';
  currency: 'USD';
  contractorHoursPerWeekDefault: number;
  contractorHourlyRateUsdDefault: number | null;
  contractorHourlyRateUsdByCapabilityId: Record<string, number>;
  updatedAt: string | null;
  updatedByUserId: string | null;
};

export async function getPortfolioPlannerAssumptions(): Promise<PortfolioPlannerAssumptions> {
  const res = await apiFetch('/v1/portfolio-planner/assumptions', { cache: 'no-cache' });
  return handle(res);
}

export async function upsertPortfolioPlannerAssumptions(body: {
  schemaVersion: string;
  contractorHoursPerWeekDefault?: number;
  contractorHourlyRateUsdDefault?: number | null;
  contractorHourlyRateUsdByCapabilityId?: Record<string, number>;
}): Promise<PortfolioPlannerAssumptions> {
  const res = await apiFetch('/v1/portfolio-planner/assumptions', {
    method: 'PUT',
    json: body,
  });
  return handle(res);
}

export type OrgCapacityTruthImportResult = {
  imported: number;
  warnings: Array<{ code: string; message: string }>;
};

export async function importOrgCapacityTruth(body: {
  orgStateId: string;
  scopeRootNodeId: string;
  items: Array<{
    positionId: string;
    hoursPerWeek?: number | null;
    fte?: number | null;
    workerType?: string | null;
    sourceType?: string | null;
  }>;
  options?: {
    allowNonAsIsOrgState?: boolean;
    strictPositionMatch?: boolean;
  };
}): Promise<OrgCapacityTruthImportResult> {
  const res = await apiFetch('/v1/portfolio-planner/org-capacity/import', {
    method: 'POST',
    json: body,
  });
  return handle(res);
}

export type OrgCapacityTruthCoverageResponse = {
  schemaVersion: 'org_capacity_truth_coverage_response_v1';
  generatedAt: string;
  orgStateId: string;
  scopeRootNodeId: string;
  coverage: {
    schemaVersion: 'org_capacity_truth_coverage_v1';
    generatedAt: string;
    orgStateId: string;
    scopeRootNodeId: string;
    filledPositions: number;
    positionsWithHoursPerWeek: number;
    positionsWithFte: number;
    positionsMissing: number;
    coveragePct: number;
  };
  missingPositionIds: string[];
};

export async function getOrgCapacityTruthCoverage(params: {
  orgStateId: string;
  scopeRootNodeId: string;
}): Promise<OrgCapacityTruthCoverageResponse> {
  const qs = new URLSearchParams({
    orgStateId: params.orgStateId,
    scopeRootNodeId: params.scopeRootNodeId,
  });
  const res = await apiFetch(`/v1/portfolio-planner/org-capacity/coverage?${qs.toString()}`, { cache: 'no-cache' });
  return handle(res);
}

export async function getPortfolioPlannerPlan(planId: string): Promise<{
  planId: string;
  strategyId: string;
  scopeRootNodeId: string;
  kernelVersion: string;
  asOf: string;
  inputsHash: string;
  sourceHash: string;
  hpx: Record<string, unknown>;
}> {
  const res = await apiFetch(`/v1/portfolio-planner/plans/${encodeURIComponent(planId)}`, { cache: 'no-cache' });
  return handle(res);
}

export async function listPortfolioPlannerStrategySnapshots(params: {
  strategyId: string;
  scopeRootNodeId: string;
  limit?: number;
}): Promise<{
  snapshots: Array<{ id: string; asOf: string; inputsHash: string; initiativeCount: number; createdAt: string }>;
}> {
  const qs = new URLSearchParams({
    strategyId: params.strategyId,
    scopeRootNodeId: params.scopeRootNodeId,
  });
  if (params.limit != null) qs.set('limit', String(params.limit));
  const res = await apiFetch(`/v1/portfolio-planner/strategy-snapshots?${qs.toString()}`, { cache: 'no-cache' });
  return handle(res);
}

export async function getPortfolioPlannerStrategySnapshot(strategySnapshotId: string): Promise<{
  id: string;
  strategyId: string;
  scopeRootNodeId: string;
  asOf: string;
  inputsHash: string;
  initiativeCount: number;
  initiatives: Array<{ initiativeId: string; title: string }>;
}> {
  const res = await apiFetch(`/v1/portfolio-planner/strategy-snapshots/${encodeURIComponent(strategySnapshotId)}`, { cache: 'no-cache' });
  return handle(res);
}

export async function buildPortfolioPlannerStrategySnapshot(body: {
  schemaVersion: string;
  strategyId: string;
  packVersionId?: string;
  scopeRootNodeId: string;
  asOf: string;
  initiatives: Array<{
    initiativeId: string;
    title: string;
    ownerNodeId?: string | null;
    strategicOwnerNodeId?: string | null;
    deliveryOwnerNodeId?: string | null;
    sizeBand: 'XS' | 'S' | 'M' | 'L' | 'XL';
    confidence: 'low' | 'med' | 'high' | null;
    startDate: string | null;
    deadlineDate: string;
    capabilityMix: Array<{ capabilityId: string; weight: number }>;
    valueModel: {
      valueType: 'revenue' | 'cost_savings' | 'risk_avoidance' | 'compliance';
      annualBenefitUsd?: number;
      annualBenefitBand?: 'LOW' | 'MED' | 'HIGH';
    };
  }>;
}): Promise<{ id: string; inputsHash: string; initiativeCount: number; asOf: string }> {
  const res = await apiFetch('/v1/portfolio-planner/strategy-snapshots/build', {
    method: 'POST',
    json: body,
  });
  return handle(res);
}

export async function clonePortfolioPlannerStrategySnapshot(body: {
  sourceStrategySnapshotId: string;
  targetScopeRootNodeId: string;
}): Promise<{ id: string; inputsHash: string; initiativeCount: number; asOf: string; clonedFromId: string }> {
  const res = await apiFetch('/v1/portfolio-planner/strategy-snapshots/clone', {
    method: 'POST',
    json: body,
  });
  return handle(res);
}

export async function publishPortfolioPlannerPlan(params: {
  planId: string;
  strategyId: string;
  scopeRootNodeId: string;
  note?: string | null;
  approvalRequestId?: string | null;
  forcePublish?: boolean;
}): Promise<{ publicationId: string }> {
  const res = await apiFetch(`/v1/portfolio-planner/plans/${encodeURIComponent(params.planId)}/publish`, {
    method: 'POST',
    json: {
      schemaVersion: 'portfolio_planner_plan_publish_v1',
      strategyId: params.strategyId,
      scopeRootNodeId: params.scopeRootNodeId,
      note: params.note ?? null,
      approvalRequestId: params.approvalRequestId ?? null,
      forcePublish: Boolean(params.forcePublish ?? false),
    },
  });
  return handle(res);
}

export async function requestPublishPortfolioPlannerApproval(params: {
  planId: string;
  strategyId: string;
  scopeRootNodeId: string;
  note?: string | null;
}): Promise<{ approvalRequestId: string; state: string; history: Array<Record<string, unknown>> }> {
  const res = await apiFetch(`/v1/portfolio-planner/plans/${encodeURIComponent(params.planId)}/publish-approvals/request`, {
    method: 'POST',
    json: {
      strategyId: params.strategyId,
      scopeRootNodeId: params.scopeRootNodeId,
      note: params.note ?? null,
    },
  });
  return handle(res);
}

async function decidePublishPortfolioPlannerApproval(params: {
  approvalRequestId: string;
  decision: 'approve' | 'reject' | 'cancel';
  note?: string | null;
}): Promise<{ approvalRequestId: string; state: string; history: Array<Record<string, unknown>> }> {
  const res = await apiFetch(`/v1/portfolio-planner/publish-approvals/${encodeURIComponent(params.approvalRequestId)}/${params.decision}`, {
    method: 'POST',
    json: { note: params.note ?? null },
  });
  return handle(res);
}

export async function approvePublishPortfolioPlannerApproval(params: {
  approvalRequestId: string;
  note?: string | null;
}): Promise<{ approvalRequestId: string; state: string; history: Array<Record<string, unknown>> }> {
  return decidePublishPortfolioPlannerApproval({
    approvalRequestId: params.approvalRequestId,
    decision: 'approve',
    note: params.note ?? null,
  });
}

export async function rejectPublishPortfolioPlannerApproval(params: {
  approvalRequestId: string;
  note?: string | null;
}): Promise<{ approvalRequestId: string; state: string; history: Array<Record<string, unknown>> }> {
  return decidePublishPortfolioPlannerApproval({
    approvalRequestId: params.approvalRequestId,
    decision: 'reject',
    note: params.note ?? null,
  });
}

export async function cancelPublishPortfolioPlannerApproval(params: {
  approvalRequestId: string;
  note?: string | null;
}): Promise<{ approvalRequestId: string; state: string; history: Array<Record<string, unknown>> }> {
  return decidePublishPortfolioPlannerApproval({
    approvalRequestId: params.approvalRequestId,
    decision: 'cancel',
    note: params.note ?? null,
  });
}

export async function runPortfolioPlannerPlan(body: {
  schemaVersion: string;
  strategyId: string;
  asOf: string;
  kernelVersion: string;
  snapshots: {
    orgSnapshotId: string;
    strategySnapshotId: string;
    executionSnapshotId: string | null;
    executionSnapshotArtifactId?: string | null;
  };
  planInputs: {
    scopeRootNodeId: string;
    timeHorizonWeeks: number;
    initiativeIds: string[];
    constraints?: {
      mustDoInitiativeIds?: string[];
      protectedInitiativeIds?: string[];
      maxCashCostUsd?: number;
      maxDisruptionScore?: number;
    } | null;
  };
}): Promise<{ planId: string; inputsHash: string }> {
  const res = await apiFetch('/v1/portfolio-planner/plans/run', {
    method: 'POST',
    json: body,
  });
  return handle(res);
}

export async function simulatePortfolioPlannerScenario(body: ScenarioSimulateInput): Promise<PortfolioScenarioComparison> {
  const res = await apiFetch('/v1/portfolio-planner/scenarios/simulate', {
    method: 'POST',
    json: body,
  });
  return handle(res);
}

export async function getActiveCapabilityTaxonomy(): Promise<{
  active:
    | ({
        id: string;
        version: number;
        label: string | null;
        csvHash: string;
        inputsHash: string;
        createdAt: string;
        createdByUserId: string | null;
        entries: Array<{
          capabilityId: string;
          name: string;
          description: string;
          category: string;
          taxonomyPathJson: unknown;
          keywordsJson: unknown;
        }>;
      })
    | null;
}> {
  const res = await apiFetch('/v1/config/capabilities/taxonomy/active', { cache: 'no-cache' });
  return handle(res);
}
